import {
  Autocomplete,
  Box,
  CircularProgress,
  ListItemText,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

const SelectCustomerUserStep = ({
  selectedCustomerUser,
  setSelectedCustomerUser,
  selectedCustomerContractor,
  setSelectedCustomerContractor,
  onCardsScroll,
  setShouldExpandOnScroll,
  descriptionTextRef,
  projectNameFieldRef,
}) => {
  const theme = useTheme();
  const isMediumDown = useMediaQuery(theme.breakpoints.down("md"));

  const currentSession = useSelector((state) => state.sessions.currentSession);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerInputValue, setCustomerInputValue] = useState("");
  const [customerLoading, setCustomerLoading] = useState(false);
  const [contractorOptions, setContractorOptions] = useState([]);
  const [contractorLoading, setContractorLoading] = useState(false);

  const [scrollSectionHeight, setScrollSectionHeight] = useState(0);

  useEffect(() => {
    const updateHeight = () => {
      const height =
        (isMediumDown ? 340 : 290) +
        (descriptionTextRef?.current?.offsetHeight ?? 0) +
        (projectNameFieldRef?.current?.offsetHeight ?? 0);
      setScrollSectionHeight(`calc(100vh - ${height}px)`);
    };

    const observer = new ResizeObserver(updateHeight);

    if (descriptionTextRef?.current)
      observer.observe(descriptionTextRef.current);
    if (projectNameFieldRef?.current)
      observer.observe(projectNameFieldRef.current);

    updateHeight();

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const listboxProps = {
    style: {
      maxHeight: 320,
      overflow: "auto",
    },
  };

  useEffect(() => {
    let active = true;

    const fetchUsers = async () => {
      if ((customerInputValue || "").trim().length < 1) {
        setCustomerOptions(selectedCustomerUser ? [selectedCustomerUser] : []);
        return;
      }

      try {
        setCustomerLoading(true);
        const resp = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/admin/getUsers`,
          {
            page: 0,
            rowsPerPage: 10,
            search: customerInputValue.trim(),
            currentSession,
            currentPath: window.location.pathname,
          },
          { withCredentials: true },
        );

        if (!active) return;

        const rows = resp?.data?.data?.rows || [];
        setCustomerOptions(rows);
      } catch (error) {
        if (active) {
          setCustomerOptions([]);
        }
      } finally {
        if (active) setCustomerLoading(false);
      }
    };

    const timeout = setTimeout(fetchUsers, 300);

    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [currentSession, customerInputValue, selectedCustomerUser]);

  useEffect(() => {
    let active = true;

    const fetchContractors = async () => {
      if (!selectedCustomerUser?.email) {
        setContractorOptions([]);
        setSelectedCustomerContractor(null);
        return;
      }

      try {
        setContractorLoading(true);
        const resp = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/admin/getUserContractors`,
          {
            email: selectedCustomerUser.email,
            currentSession,
            currentPath: window.location.pathname,
          },
          { withCredentials: true },
        );

        if (!active) return;

        const contractorRows = resp?.data?.data?.contractors || [];
        setContractorOptions(contractorRows);
      } catch (error) {
        if (active) {
          setContractorOptions([]);
        }
      } finally {
        if (active) setContractorLoading(false);
      }
    };

    fetchContractors();

    return () => {
      active = false;
    };
  }, [currentSession, selectedCustomerUser, setSelectedCustomerContractor]);

  const customerHelperText = useMemo(() => {
    if ((customerInputValue || "").trim().length < 1) {
      return "Type a customer name, contractor/company name, or email.";
    }

    if (!customerLoading && customerOptions.length === 0) {
      return "No matching customers found.";
    }

    return "Select the customer who should own this permit package.";
  }, [customerInputValue, customerLoading, customerOptions.length]);

  const contractorHelperText = useMemo(() => {
    if (!selectedCustomerUser?.email) {
      return "Select a customer first, then choose their contractor.";
    }

    if (!contractorLoading && contractorOptions.length === 0) {
      return "This customer has no contractors available.";
    }

    return "Select the specific contractor for this customer.";
  }, [selectedCustomerUser, contractorLoading, contractorOptions.length]);

  const getCustomerOptionLabel = (option) => {
    if (!option) return "";
    return [option.customerName, option.contractorName, option.email]
      .filter(Boolean)
      .join(" — ");
  };

  const getContractorOptionLabel = (option) => {
    if (!option) return "";
    return [
      option.businessName,
      [option.firstName, option.lastName].filter(Boolean).join(" "),
      option.licenseNumber,
    ]
      .filter(Boolean)
      .join(" — ");
  };

  return (
    <Box>
      <Box sx={{ borderBottom: "3px solid #d3d3d3", mb: 2 }} />

      <Box
        sx={{
          maxWidth: 700,
          mx: "auto",
          px: { xs: 1, sm: 2 },
          height: scrollSectionHeight,
          overflowY: "auto",
        }}
        onScroll={(event) => {
          if (!onCardsScroll) return;
          const shouldCondense = event.currentTarget.scrollTop > 20;
          onCardsScroll(shouldCondense);
          if (isMediumDown) {
            setShouldExpandOnScroll(shouldCondense);
          }
        }}
      >
        <Typography align="center" variant="body1" sx={{ mb: 2 }}>
          Search for the customer who should own this permit package.
        </Typography>

        <Autocomplete
          fullWidth
          options={customerOptions}
          freeSolo={false}
          loading={customerLoading}
          value={selectedCustomerUser || null}
          inputValue={customerInputValue}
          onInputChange={(_, value) => setCustomerInputValue(value)}
          onChange={(_, value) => {
            setSelectedCustomerUser(value || null);
            setSelectedCustomerContractor(null);
          }}
          isOptionEqualToValue={(option, value) =>
            option?.email === value?.email
          }
          getOptionLabel={getCustomerOptionLabel}
          filterOptions={(x) => x}
          ListboxProps={listboxProps}
          noOptionsText={
            (customerInputValue || "").trim().length < 1
              ? "Start typing to search"
              : "No customers found"
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search customer"
              helperText={customerHelperText}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {customerLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option?.email || option?.id}>
              <Box>
                <Typography fontWeight={700}>
                  {option?.customerName || "Unnamed Customer"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {[
                    option?.contractorName,
                    option?.email,
                    option?.customerPhone,
                  ]
                    .filter(Boolean)
                    .join(" • ")}
                </Typography>
              </Box>
            </Box>
          )}
        />

        <Autocomplete
          fullWidth
          sx={{ mt: 2 }}
          options={contractorOptions}
          freeSolo={false}
          loading={contractorLoading}
          disabled={!selectedCustomerUser?.email}
          value={selectedCustomerContractor || null}
          onChange={(_, value) => setSelectedCustomerContractor(value || null)}
          isOptionEqualToValue={(option, value) =>
            option?.licenseNumber === value?.licenseNumber &&
            option?.email === value?.email
          }
          getOptionLabel={getContractorOptionLabel}
          filterOptions={(x) => x}
          ListboxProps={listboxProps}
          noOptionsText={
            selectedCustomerUser?.email
              ? "No contractors found"
              : "Select a customer first"
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select contractor"
              helperText={contractorHelperText}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {contractorLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <Box
              component="li"
              {...props}
              key={[option?.licenseNumber, option?.email, option?.businessName]
                .filter(Boolean)
                .join("-")}
            >
              <ListItemText
                primary={
                  option?.businessName ||
                  [option?.firstName, option?.lastName]
                    .filter(Boolean)
                    .join(" ") ||
                  "Unnamed Contractor"
                }
                secondary={[
                  [option?.firstName, option?.lastName]
                    .filter(Boolean)
                    .join(" "),
                  option?.licenseNumber,
                  option?.email,
                ]
                  .filter(Boolean)
                  .join(" • ")}
              />
            </Box>
          )}
        />
      </Box>
    </Box>
  );
};

export default SelectCustomerUserStep;
